import { zodResolver } from '@hookform/resolvers/zod';
import type {
  ActiveShift,
  DesktopCatalogue,
  DesktopSales,
  DesktopShift,
  DesktopSync,
  DesktopTickets,
  HeldCatalogueItem,
  HeldRegistration,
  IssuedTicket,
  RecordedSale,
  SaleRecordRequest,
} from '@repo/desktop-bridge';
import { isTaxed, priceBasket } from '@repo/desktop-bridge';
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import {
  IconAlertTriangle,
  IconCircleCheck,
  IconMinus,
  IconPlus,
  IconPrinterOff,
  IconReceipt,
  IconTrash,
} from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import type { Clock } from '@/lib/clock';
import { formatPaise } from '@/lib/money';
import { newUuidv7 } from '@/lib/uuidv7';

import { fieldLabel, fieldSurface } from '../auth/AuthFrame';
import { adultsThenChildren } from './admission-order';
import {
  byOutlet,
  grandTotalPaise,
  lineKey,
  type LineOutlet,
  type OutletGroup,
  type OutletLine,
  type OutletSale,
  recordByOutlet,
} from './basket-outlets';
import { cashTenderedField, cashTenderedOnRequest } from './cash-given';
import { CashGiven } from './CashGiven';
import {
  cashCarriesNoReference,
  PAYMENT_MODES,
  paymentModeField,
  paymentModeLabel,
  paymentReferenceField,
  paymentReferenceOnWire,
  takesPaymentReference,
} from './payment-modes';
import { QuantityField, TYPED_QUANTITY_MAX } from './QuantityField';
import { rateBoardScale } from './rate-board';
import {
  messageForJoinRefusal,
  messageForSaleRefusal,
  messageForTicketRefusal,
  TILL_UNAVAILABLE,
  TILL_UNAVAILABLE_FOR_PARTY,
  UNTAXED_ACROSS_OUTLETS,
} from './till-messages';
import { TillActions, tillPlate, tillScroll } from './TillFrame';
import { timeSlotFields, timeSlotOnRequest } from './time-slot';
import { TimeSlotFields } from './TimeSlotFields';
import { TodaysSales } from './TodaysSales';

/**
 * Selling admission at the gate and at the activity centres.
 *
 * **A party, then the payment — issue #253, and the order `SellGoods` uses.**
 * The counter sells to families in a queue, so a cashier taps what they want —
 * each tap adds one, and the plate shows how many — answers how they paid once,
 * and presses Take payment once. Four adults used to be four payment answers,
 * four presses and four sales; it is now one of each.
 *
 * **A party on a taxed price is one sale (#274)** — a line per item with its
 * quantity, under one tax invoice number from this till's `INVOICE` counter,
 * recorded through the shop counter's own `sales.record` (#269). `ADULT 2` and
 * `CHILD 1` are two lines, not three records. **The invoice is the entry
 * ticket**: it prints headed `ENTRY TICKET`, there are no per-person ticket
 * numbers, and the correction is a refund of the whole sale rather than a void
 * per person — a manager's, in the back office (#294). In production every admission is taxed, so this is the gate.
 *
 * **Admission on an untaxed price is still a ticket per person**, as before:
 * the API refuses it on a sale. The main process sells those one after another
 * under the one answer (`issue-basket.ts`) and prints one bill for them (#263).
 * A party that mixes the two is one sale for the taxed lines and tickets for the
 * rest. The rule is per price row, read off the copy this till holds.
 *
 * A party of 175 is typed, not tapped.
 *
 * **The till numbers first and tells the server afterwards.** Each admission
 * advances this terminal's own counter and queues the value; the server records
 * that number rather than issuing one, because what the visitor is holding is
 * the identifier of record.
 * Nothing on this screen waits for the API and nothing on it reports whether the
 * API has heard yet — the sale is complete when the paper is in a hand.
 *
 * **Three ordinary things this screen deliberately does not warn about**, each
 * of which a careful-looking implementation would: a gap in the sequence (a till
 * that printed three and crashed before syncing two is a normal Tuesday),
 * tickets reaching the server out of order, and a queue with items in it. The
 * last is worth stating because it is the one on screen: `n waiting to send` is
 * drawn as a plain count in the till's own lettering, not as a warning, because
 * offline is an operating mode here rather than a fault
 * (`docs/ui-conventions.md`).
 *
 * **How they paid is asked every time, and it has no default.** Every ticket
 * carries exactly one mode to the ledger — a `NULL` there is unrepairable,
 * because `ledger_entries` is append-only by database trigger — and the mode is
 * what Day Close reconciles the drawer against: a card admission recorded as
 * cash is a drawer short by exactly that amount with nothing on the screen
 * having looked wrong at the time. So Take payment does not sell until a mode is
 * chosen, and the mode clears after each party — the tap that carries over is
 * the one nobody looked at. It is asked last now, as the shop asks it, rather
 * than first: it is still asked before anything is spent, which is the whole of
 * what the old order was for.
 *
 * **A party is never recorded twice, and a part-sold one says so.** The basket
 * is sent under a key minted for it and held until it is fully sold; the main
 * process never sells an admission twice under one key, so pressing again after
 * a failure sells only what is missing. While a party stands part-sold, nothing
 * that would change what the retry sends can be touched.
 *
 * **There is no reprint and there is no button that implies one.** The printed
 * artefact is the only copy; a visitor who loses one buys another. A receipt is
 * the record of a payment already taken; a ticket — and an admission invoice,
 * which is one — admits somebody, and a second copy is a free admission
 * (CL-001); the API refuses a reprint of any sale with admission on it.
 *
 * **Nothing on a sold party is corrected here.** A ticket stopped being voided
 * at the gate in #285, and the sale's refund followed it in #294: an operator
 * who can reverse a sale at the counter can take the cash back out of the drawer
 * against a visitor who has already left. Both are a manager's, in the back
 * office, and every sentence on this screen that would have offered one now says
 * to take the number to a manager. A mis-counted party already paid for — forty
 * charged instead of four, the visitor still at the window — is that walk too,
 * and it is the park's call that it should be.
 *
 * **One basket across the outlets this till holds (#284).** A counter that
 * sells for Fish Spa and Glass Bridge rings both up for one visitor, quotes one
 * total and takes one payment — and records **a sale per outlet**, each into
 * that outlet's own registration with its own numbers and slip, all carrying
 * the one mode and reference. Another outlet *joins* the sale when the cashier
 * picks its board, and one they may not sell at is refused then, by name.
 * Lines from another outlet are taxed admission only; see `basket-outlets.ts`.
 * The outlets record one after another and stop at the first that does not,
 * which is the part-sold party below with outlets in it: what recorded stays
 * sold and on screen, Try again records only the rest, and what recorded is a
 * manager's to refund if the visitor no longer wants it.
 *
 * The plates are deliberately **not** the accent colour. One primary action per
 * screen is the rule, and here, as on the shop counter, the green is spent on
 * Take payment.
 */

/** The two kinds that can be printed as admission. A `PRODUCT` belongs to Sales. */
const ADMISSION_KINDS: readonly HeldCatalogueItem['kind'][] = ['TICKET_TYPE', 'ACTIVITY'];

/** What each kind is called on this screen, and the order they are drawn in. */
const SECTIONS: readonly { kind: HeldCatalogueItem['kind']; label: string }[] = [
  { kind: 'TICKET_TYPE', label: 'Admission' },
  { kind: 'ACTIVITY', label: 'Activities' },
];

/**
 * The payment, as a schema rather than as a pair of `useState`s.
 *
 * `docs/ui-conventions.md`: the schema is the validation, and there is no second
 * way to build a form. Both fragments and the cash rule come from
 * `payment-modes.ts` — `SellGoods` asks a customer the same two questions.
 */
const paymentSchema = z
  .object({
    paymentMode: paymentModeField,
    paymentReference: paymentReferenceField,
    // What the party handed over, for the change and the bill (#269). The
    // shop's own field — see `cash-given.ts`.
    cashTendered: cashTenderedField,
    // The visitor's window, where the outlet asks (#277) — `time-slot.ts`.
    ...timeSlotFields,
  })
  // The rule is shared with the sale; the sentence is not. A ticket is not a
  // basket to the person reading the refusal.
  .refine(cashCarriesNoReference, {
    path: ['paymentReference'],
    message: 'A cash ticket cannot carry a payment reference.',
  });

type PaymentValues = z.infer<typeof paymentSchema>;

/**
 * One line of the party. Merged by outlet and item, never by tap — `outlet` is
 * `null` at a till that sells for one outlet, and on every line it rings up.
 */
type BasketLine = OutletLine;

/**
 * A party the till sold only in part, or did not answer for.
 *
 * **While one stands the basket is held**: it will be sent again exactly as it
 * was, under the same key, so the till can sell what is missing and nothing
 * twice.
 */
interface Stopped {
  /**
   * What recorded as sales before the rest stopped: the party's taxed lines as
   * one sale, or one sale per outlet that recorded (#284).
   */
  readonly sales: readonly OutletSale[];
  /** What was sold and printed. Empty when the till did not answer. */
  readonly issued: readonly IssuedTicket[];
  /** What was not, said line by line — `null` when the till did not answer, so nobody knows. */
  readonly unsold: readonly { readonly key: string; readonly text: string }[] | null;
  /** The outlet a basket across outlets stopped at (#284); `null` for a party at one. */
  readonly at: string | null;
  readonly message: string;
}

/** What a party came to: its sales — one, or one per outlet (#284) — its tickets, or both. */
interface Party {
  readonly sales: readonly OutletSale[];
  readonly tickets: readonly IssuedTicket[];
}

/** The one sale a party at one outlet records, as a list of the sales a party came to. */
const atThisOutlet = (sale: RecordedSale | null): OutletSale[] =>
  sale === null ? [] : [{ outlet: null, sale }];

/**
 * The other outlets this till sells for, and the slice that sells into them
 * (#284). Given only where the till holds more than one registration and the
 * shell can record into another; absent, the screen is exactly what it was.
 */
export interface Across {
  /** What the outlet the counter is on is called: the first group, and its board. */
  readonly here: string;
  /** The others, live — each one a board the cashier can ring up from. */
  readonly outlets: readonly HeldRegistration[];
  /** `joinOutlet` and `recordAt`, on the shop counter's slice. */
  readonly sales: DesktopSales;
  /** Prefills the visitor's window where an outlet that joined asks for one (#277). */
  readonly clock: Clock;
}

interface Bridge {
  catalogue: DesktopCatalogue;
  tickets: DesktopTickets;
  /**
   * The shop counter's slice, which records a taxed party as one sale (#274).
   * `null` on a shell that cannot, which then sells every admission as tickets
   * — exactly as the gate did before — rather than not selling.
   */
  sales: DesktopSales | null;
  /**
   * The slice that lists the day's sales — the shop's Today's sales, offered
   * here too (#290) — or `null` on a shell that cannot.
   */
  recent: DesktopSales | null;
  shift: DesktopShift;
  sync: DesktopSync | null;
}

export function SellAdmission({
  bridge,
  shift,
  onShiftEnded,
  timeSlot,
  across,
}: {
  bridge: Bridge;
  shift: ActiveShift;
  onShiftEnded: () => void;
  /** Where this till sells for more outlets than this one (#284). */
  across?: Across | undefined;
  /**
   * Set where this outlet asks for the visitor's time slot (#277), with the
   * clock that prefills it. Asked only of a party with a line on the invoice:
   * the invoice is the paper that prints it.
   */
  timeSlot?: { clock: Clock } | undefined;
}) {
  const [items, setItems] = useState<readonly HeldCatalogueItem[] | null>(null);
  const [waiting, setWaiting] = useState<number | null>(null);

  const [basket, setBasket] = useState<readonly BasketLine[]>([]);

  /**
   * The key this basket is sold under, minted at the first press and held until
   * the party is fully sold or given up on.
   *
   * **This is what stops a retry recording a line twice.** The main process
   * remembers which admissions it sold under a key and never sells them again,
   * so a press after a part-sale or a lost answer sells only what is missing.
   * A ref rather than state: nothing renders from it.
   */
  const basketKey = useRef<string | null>(null);

  /**
   * Whose board is on screen (#284): `null` for the counter's own outlet, or
   * another that has joined this sale and what it may sell into it. Joined
   * again each time it is chosen, so its prices and its roster are current.
   */
  const [board, setBoard] = useState<{
    readonly outlet: HeldRegistration;
    readonly items: readonly HeldCatalogueItem[];
  } | null>(null);
  const [joining, setJoining] = useState(false);

  /** The party just sold, kept on screen until the next one replaces it. */
  const [sold, setSold] = useState<Party | null>(null);
  const [stopped, setStopped] = useState<Stopped | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  /**
   * Whether Today's sales is pushed over the counter (#290). The counter stays
   * mounted under it, so a party half rung up is still there on the way back —
   * the visitor at the front of the queue is not the one who came back.
   */
  const [today, setToday] = useState(false);

  const form = useForm({
    resolver: zodResolver(paymentSchema),
    mode: 'onTouched',
    // No `paymentMode`, deliberately — see the note at the top of this file. The
    // reference starts empty rather than absent so the input is controlled from
    // the first render.
    defaultValues: { paymentReference: '', cashTendered: '' },
  });

  const paymentMode = form.watch('paymentMode');

  /** Only UPI and card may carry a reference; cash is refused one outright. */
  const takesReference = takesPaymentReference(paymentMode);

  /**
   * Whether a party is being sold. **Every control that changes the sale is off
   * while it is**, not merely the one pressed — a second press on a party in
   * flight is the double sale this screen most exists to prevent, and the key
   * makes it harmless even if one got through.
   */
  const pending = form.formState.isSubmitting;

  /** A part-sold or unanswered party holds the basket until it is settled. */
  const held = stopped !== null;

  const readQueue = useCallback(() => {
    bridge.sync
      ?.status()
      .then((counts) => {
        setWaiting(counts.pending + counts.inFlight);
      })
      .catch((error: unknown) => {
        // Nothing on screen. The count is a courtesy, and an alarm about a
        // failed IPC call is a worse thing to put in front of a cashier than a
        // missing number.
        console.error('till: the queue could not be read', error);
      });
  }, [bridge.sync]);

  useEffect(() => {
    let live = true;

    // Read from the held copy with no round trip. The version poll in the main
    // process is what keeps it current; a screen must not fetch on mount.
    bridge.catalogue
      .held()
      .then((catalogue) => {
        if (live) setItems(catalogue?.items ?? []);
      })
      .catch((error: unknown) => {
        console.error('till: the catalogue could not be read', error);
        if (live) setItems([]);
      });

    readQueue();

    return () => {
      live = false;
    };
  }, [bridge.catalogue, readQueue]);

  /** One more of `item`, sold at `outlet` — the counter's own unless another has joined. */
  const add = (item: HeldCatalogueItem, outlet: LineOutlet = null) => {
    setRefusal(null);

    const key = lineKey(outlet, item.id);
    const next = basket.some((line) => lineKey(line.outlet, line.item.id) === key)
      ? basket.map((line) =>
          // Held at the box's ceiling, as the shop holds at its own: each one
          // is a ticket, and the box could not show a fourth digit.
          lineKey(line.outlet, line.item.id) === key
            ? { ...line, quantity: Math.min(line.quantity + 1, TYPED_QUANTITY_MAX) }
            : line,
        )
      : [...basket, { item, quantity: 1, outlet }];

    // **A basket across outlets is sales only (#284)**: an untaxed admission is
    // a ticket rather than a sale line, and cannot share a payment that is a
    // sale per outlet. Refused at the tap, not at Take payment with the money
    // in a hand.
    if (next.some((line) => line.outlet !== null) && next.some((line) => !isTaxed(line.item))) {
      setRefusal(UNTAXED_ACROSS_OUTLETS);

      return;
    }

    setBasket(next);
  };

  /** A typed quantity (#263) — already a whole number from 1 to the ceiling. */
  const setQuantity = (key: string, quantity: number) => {
    setRefusal(null);
    setBasket((lines) =>
      lines.map((line) =>
        lineKey(line.outlet, line.item.id) === key ? { ...line, quantity } : line,
      ),
    );
  };

  /** One fewer, and the line goes when the last one does. */
  const removeOne = (key: string) => {
    setRefusal(null);

    setBasket((lines) =>
      lines.flatMap((line) => {
        if (lineKey(line.outlet, line.item.id) !== key) return [line];

        return line.quantity <= 1 ? [] : [{ ...line, quantity: line.quantity - 1 }];
      }),
    );
  };

  const removeLine = (key: string) => {
    setRefusal(null);
    setBasket((lines) => lines.filter((line) => lineKey(line.outlet, line.item.id) !== key));
  };

  /** What an outlet is called on this screen: the counter's own, or one that joined. */
  const outletName = (outlet: LineOutlet): string =>
    outlet === null ? (across?.here ?? '') : (outlet.outletName ?? outlet.terminal.prefix);

  /**
   * Show `outlet`'s board (#284) — joining it to this sale, which admits the
   * cashier there by its own roster and leaves the counter where it is. An
   * outlet they may not sell at is refused here, by name, before anything is
   * rung up from it.
   */
  const choose = async (outlet: HeldRegistration | null) => {
    setRefusal(null);

    if (outlet === null || across === undefined) {
      setBoard(null);

      return;
    }

    setJoining(true);

    try {
      const outcome = await across.sales.joinOutlet(outlet.id);

      if (outcome.joined) {
        setBoard({ outlet, items: outcome.items });
      } else {
        setRefusal(messageForJoinRefusal(outcome.reason, outletName(outlet)));
      }
    } catch (error) {
      console.error('till: another outlet could not join the sale', error);
      setRefusal(TILL_UNAVAILABLE);
    } finally {
      setJoining(false);
    }
  };

  /** The party is finished with: the next basket is a new sale under a new key. */
  const finish = (party: Party) => {
    setSold(party.sales.length === 0 && party.tickets.length === 0 ? null : party);
    setStopped(null);
    setBasket([]);
    basketKey.current = null;
    // The mode goes with the party it belonged to. Left standing, the next one
    // through the gate is recorded however the last one paid, by nobody having
    // looked.
    form.reset({ paymentReference: '', cashTendered: '' });
  };

  const sales = bridge.sales;

  /**
   * **Which lines go on the invoice (#274):** admission on a taxed price, on a
   * shell that can record a sale. The rest are tickets. Per line, from the price
   * row this till holds, exactly as the main process will judge it.
   */
  const onInvoice = (line: BasketLine) => sales !== null && isTaxed(line.item);
  const invoiced = basket.filter(onInvoice);
  const ticketed = basket.filter((line) => !onInvoice(line));

  /**
   * The invoice priced — **what will be charged, shown before the money is
   * taken.** A taxed line charges taxable + tax, not the sticker price times the
   * quantity, and the invoice is taken to the whole rupee: 7 adults at ₹100 and
   * 3 children at ₹50 are lines of ₹700.04 and ₹149.99 and a total of ₹850.00.
   * `priceBasket` is the one function the main process records the sale with,
   * so the figure here is the figure charged. `null` only past what a number
   * can hold, which the main process refuses.
   */
  const priced = priceBasket(invoiced.map(({ item, quantity }) => ({ quantity, price: item })));

  /**
   * **A sale per outlet (#284)** once any line is another outlet's: each
   * outlet's lines are its own invoice, priced and rounded on their own. `null`
   * for a party at the counter's outlet alone, which is priced as it always was.
   */
  const groups = basket.some((line) => line.outlet !== null) ? byOutlet(basket) : null;

  /** What each line charges, by line — the invoice's figure, or a ticket's sticker sum. */
  const amounts = new Map<string, number>(
    groups === null
      ? [
          ...invoiced.map((line, index): [string, number] => [
            lineKey(line.outlet, line.item.id),
            priced?.lines[index]?.amountPaise ?? 0,
          ]),
          ...ticketed.map((line): [string, number] => [
            lineKey(line.outlet, line.item.id),
            line.quantity * line.item.sellingPaise,
          ]),
        ]
      : groups.flatMap((group) =>
          group.lines.map((line, index): [string, number] => [
            lineKey(line.outlet, line.item.id),
            group.priced?.lines[index]?.amountPaise ?? 0,
          ]),
        ),
  );

  /**
   * The running total, in integer paise: the invoice's, and the tickets' beside
   * it — or, across outlets, **each outlet's invoice added**, which is what the
   * cashier quotes and what the sales charge between them.
   */
  const totalPaise =
    groups === null
      ? (priced?.totalPaise ?? 0) +
        ticketed.reduce((sum, line) => sum + line.quantity * line.item.sellingPaise, 0)
      : (grandTotalPaise(groups) ?? 0);

  /**
   * Where the paper asks for the visitor's window (#277): the counter's own
   * outlet with a line on the invoice — or, across outlets, any outlet in the
   * basket whose rule asks. It goes to every outlet's sale, and each outlet's
   * own rule decides whether its paper prints it.
   */
  const timeSlotClock =
    groups === null
      ? timeSlot !== undefined && invoiced.length > 0
        ? timeSlot.clock
        : null
      : groups.some((group) =>
            group.outlet === null ? timeSlot !== undefined : group.outlet.timeSlot.ask,
          )
        ? (timeSlot?.clock ?? across?.clock ?? null)
        : null;

  /**
   * Record a basket across outlets: one sale per outlet, in turn, under the one
   * payment (#284).
   *
   * **The part-sold party, with outlets in it.** An outlet that records leaves
   * the basket at once, as the party's sale does before its tickets, so Try
   * again can only ever send what did not record. What did stays on screen,
   * sold and numbered; the rest is retried or cleared, deliberately.
   */
  const sellAcross = async (
    outletGroups: readonly OutletGroup[],
    payment: Omit<SaleRecordRequest, 'lines'>,
    slice: DesktopSales,
  ) => {
    const earlier = stopped?.sales ?? [];

    const result = await recordByOutlet(outletGroups, (group) => {
      // The items and how many, and the one payment — the outlet is which
      // registration it goes to, never a field on the sale.
      const request = {
        lines: group.lines.map((line) => ({
          catalogueItemId: line.item.id,
          quantity: line.quantity,
        })),
        ...payment,
      };

      return group.outlet === null
        ? slice.record(request)
        : slice.recordAt(group.outlet.id, request);
    });

    const recorded = [...earlier, ...result.recorded];

    if (result.stopped === null) {
      finish({ sales: recorded, tickets: [] });

      return;
    }

    const at = outletName(result.stopped.outlet);
    const message =
      result.stopped.reason === null
        ? TILL_UNAVAILABLE
        : messageForSaleRefusal(result.stopped.reason);

    setSold(null);

    if (recorded.length === 0) {
      // Nothing was spent at any outlet — no number, no paper, no queue entry —
      // so the basket stays open to be put right, as a refused one always has.
      // Named when it was an outlet that joined, which the cashier may not
      // think of as this counter's.
      setRefusal(result.stopped.outlet === null ? message : `${at}: ${message}`);

      return;
    }

    setBasket(result.remaining);
    setStopped({
      sales: recorded,
      issued: [],
      unsold: result.remaining.map((line) => ({
        key: lineKey(line.outlet, line.item.id),
        text: `${outletName(line.outlet)} · ${String(line.quantity)} × ${line.item.name}`,
      })),
      at,
      message,
    });
  };

  const takePayment = async (values: PaymentValues) => {
    if (pending || basket.length === 0) return;

    setRefusal(null);

    // Omitted rather than sent empty: `''` is refused by `minLength: 1` where an
    // absent key is accepted. One spelling of that, shared with the shop.
    const reference = paymentReferenceOnWire(values.paymentMode, values.paymentReference);
    const payment = {
      paymentMode: values.paymentMode,
      ...(reference === undefined ? {} : { paymentReference: reference }),
      // Printed with the balance, and sent no further. **Not on a split party**:
      // the invoice and the bill each hold part of the total, so neither could
      // print the party's change truthfully — nor can one outlet's slip of a
      // basket across outlets (#284). The screen still shows it.
      ...((invoiced.length > 0 && ticketed.length > 0) || groups !== null
        ? {}
        : cashTenderedOnRequest(values.paymentMode, values.cashTendered)),
    };

    if (groups !== null && across !== undefined) {
      try {
        await sellAcross(groups, { ...payment, ...timeSlotOnRequest(values) }, across.sales);
      } finally {
        readQueue();
      }

      return;
    }

    /** The sale this party recorded — now, or on an earlier press that stopped at its tickets. */
    let sale = stopped?.sales[0]?.sale ?? null;

    try {
      if (invoiced.length > 0 && sales !== null) {
        // The items and how many, and nothing else — the shop counter's call.
        // The amounts, the price rows, both numbers, the person and the outlet
        // are all decided in the main process from what this device holds.
        const outcome = await sales.record({
          lines: invoiced.map((line) => ({
            catalogueItemId: line.item.id,
            quantity: line.quantity,
          })),
          ...payment,
          // For the invoice only, where this outlet asks for one.
          ...timeSlotOnRequest(values),
        });

        if (!outcome.recorded) {
          // Nothing was spent — no number, no paper, no queue entry — so the
          // party stays open to be put right, as a refused basket does at the
          // shop.
          setSold(null);
          setRefusal(messageForSaleRefusal(outcome.reason));

          return;
        }

        sale = outcome.sale;

        if (ticketed.length > 0) {
          // The taxed lines are sold. What is left of the party is its tickets,
          // and a retry sends only those — so the sale is never recorded twice.
          setBasket(ticketed);
        }
      }

      if (ticketed.length === 0) {
        finish({ sales: atThisOutlet(sale), tickets: [] });

        return;
      }

      basketKey.current ??= newUuidv7();

      const outcome = await bridge.tickets.issueBasket({
        basketId: basketKey.current,
        lines: ticketed.map((line) => ({ catalogueItemId: line.item.id, quantity: line.quantity })),
        ...payment,
      });

      if (outcome.unissued.length === 0) {
        finish({ sales: atThisOutlet(sale), tickets: outcome.issued });

        return;
      }

      const message =
        outcome.reason === null
          ? TILL_UNAVAILABLE_FOR_PARTY
          : messageForTicketRefusal(outcome.reason);

      setSold(null);

      if (outcome.issued.length === 0 && sale === null) {
        // Nothing was spent under this key, so the basket stays open to be put
        // right — exactly as a refused basket does on the shop counter. The key
        // goes with it: an edited basket is a different sale, and a fresh key
        // makes that structural rather than resting on the till having sold
        // nothing under the old one.
        setStopped(null);
        basketKey.current = null;
        setRefusal(message);

        return;
      }

      setStopped({
        sales: atThisOutlet(sale),
        issued: outcome.issued,
        unsold: outcome.unissued.map((line) => ({
          key: line.catalogueItemId,
          text: `${String(line.quantity)} × ${
            basket.find((each) => each.item.id === line.catalogueItemId)?.item.name ??
            line.catalogueItemId
          }`,
        })),
        at: null,
        message,
      });
    } catch (error) {
      console.error('till: the party could not be sold', error);
      setSold(null);

      if (sale === null && invoiced.length > 0) {
        // The sale call itself did not answer — the shop counter's case, with
        // its sentence: the main process records a sale in one transaction, so
        // a throw is one that recorded nothing.
        setRefusal(TILL_UNAVAILABLE);

        return;
      }

      // No answer about the tickets, so nobody knows how much of the party was
      // sold. The basket is held and goes again under the same key, which is
      // safe whatever the answer would have been.
      setStopped({
        sales: atThisOutlet(sale),
        issued: [],
        unsold: null,
        at: null,
        message: TILL_UNAVAILABLE_FOR_PARTY,
      });
    } finally {
      // After the sale rather than on a timer: the count changes when something
      // is queued or drained.
      readQueue();
    }
  };

  const admission = (items ?? []).filter((item) => ADMISSION_KINDS.includes(item.kind));

  /** The board on screen: the counter's own admission, or what a joined outlet sells into this sale. */
  const shown = board === null ? admission : board.items;
  const boardOutlet: LineOutlet = board?.outlet ?? null;

  /**
   * How big the plates are, given how many the board is holding.
   *
   * Counted over the whole board rather than per section: Admission and
   * Activities are two headings on one wall, and scaling them apart would put a
   * large plate beside a small one for no reason a person at the gate could
   * name.
   */
  const scale = rateBoardScale(shown.length);

  /**
   * The headings that actually have something under them.
   *
   * An empty heading must not take a share of the field: each board that *is*
   * drawn is `flex-1`, so one board takes the wall and two take half each rather
   * than stacking at their own heights with mist under the last one.
   */
  const boards = SECTIONS.map(({ kind, label }) => ({
    kind,
    label,
    // Adult and Children first on every board, the counter's own or a joined
    // outlet's — see `admission-order.ts`.
    items: adultsThenChildren(shown.filter((item) => item.kind === kind)),
  })).filter((section) => section.items.length > 0);

  /** How many of each line the party holds, for the count on its plate. */
  const counted = new Map(
    basket.map((line) => [lineKey(line.outlet, line.item.id), line.quantity]),
  );

  if (today && bridge.recent !== null) {
    return (
      <TodaysSales
        sales={bridge.recent}
        onBack={() => {
          setToday(false);
        }}
      />
    );
  }

  return (
    // `min-h-0` and an internal scroll below, because `TillFrame` is `h-screen`
    // and clips rather than growing: the till rule is that a sale completes
    // without scrolling. `max-w-[84rem]` is gone and nothing replaces it, for
    // the reason `SellGoods` gives at the same line.
    <div className="flex h-full min-h-0 w-full flex-col gap-4">
      <TillActions>
        {/* A plain count in the till's own lettering, never a warning. */}
        {waiting !== null && waiting > 0 && (
          <span className={tillPlate}>
            <span className="numeric">{waiting}</span> waiting to send
          </span>
        )}

        {/* The shop's screen, the same one (#290): what this till has taken
            today, and where a correction is made (#294). Absent on a shell
            that cannot list the day. */}
        {bridge.recent !== null && (
          <Button
            variant="outline"
            size="touch"
            onClick={() => {
              setToday(true);
            }}
          >
            <IconReceipt data-icon="inline-start" />
            Today&rsquo;s sales
          </Button>
        )}

        <Button
          variant="outline"
          size="touch"
          onClick={() => {
            void bridge.shift
              .end()
              .catch((error: unknown) => {
                console.error('till: the shift could not be ended', error);
              })
              .finally(onShiftEnded);
          }}
        >
          End shift, {shift.displayName}
        </Button>
      </TillActions>

      {items === null ? (
        <ItemsSkeleton />
      ) : admission.length === 0 ? (
        <Alert>
          <IconAlertTriangle />
          <AlertTitle>There is nothing to sell here yet</AlertTitle>
          <AlertDescription>
            This till holds no admission tickets or activities for its outlet. An owner sets what an
            outlet sells in the back office; once they have, connect this terminal to the park
            network for a moment to pick it up.
          </AlertDescription>
        </Alert>
      ) : (
        // **The shop counter's shape, deliberately** — the two screens sit side
        // by side on one device, and a cashier moving between them should find
        // the sale in the same place. The board and its payment row on the
        // left, the sale on the right; `SellGoods` carries the measurements.
        <Form {...form}>
          <div className="flex min-h-0 flex-1 gap-4">
            <section className="flex min-w-0 flex-1 flex-col gap-4">
              {/* Whose board, where this till sells for more than one outlet
                  (#284) — composed from buttons as the counter choice in
                  `Till.tsx` is, and neutral for the same reason: the green is
                  Take payment's. Choosing another outlet joins it to the sale. */}
              {across !== undefined && (
                <div className="flex shrink-0 flex-wrap items-center gap-3">
                  <span className={tillPlate}>Sell for</span>
                  <div role="radiogroup" aria-label="Sell for" className="flex flex-wrap gap-2">
                    {[null, ...across.outlets].map((outlet) => (
                      <Button
                        key={outlet?.id ?? 'here'}
                        role="radio"
                        aria-checked={(boardOutlet?.id ?? null) === (outlet?.id ?? null)}
                        variant={
                          (boardOutlet?.id ?? null) === (outlet?.id ?? null)
                            ? 'secondary'
                            : 'outline'
                        }
                        size="touch"
                        disabled={pending || held || joining}
                        onClick={() => void choose(outlet)}
                      >
                        {outletName(outlet)}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* The floor is the board's own, for the reason `SellGoods` gives:
                  with the payment row sized to its content and this at `flex-1
                  min-h-0`, flex shrinking hands the row the whole of any
                  negative free space and this resolves to 0px. */}
              <div className={`flex min-h-[10rem] min-w-0 flex-1 flex-col gap-6 ${tillScroll}`}>
                {boards.map(({ kind, label, items: section }) => (
                  <section key={kind} className="flex min-h-0 flex-1 flex-col gap-4">
                    <h2 className={`shrink-0 ${tillPlate}`}>{label}</h2>

                    {/* **`flex-1` is what makes the row band's `1fr` mean
                        anything**: an `fr` needs free space to take, and a grid
                        sized to its own content has none. */}
                    <ul
                      className={`grid min-h-0 flex-1 ${scale.columns} ${scale.rows} content-start gap-4`}
                    >
                      {section.map((item) => (
                        <li key={item.id}>
                          <AdmissionPlate
                            item={item}
                            count={counted.get(lineKey(boardOutlet, item.id)) ?? 0}
                            nameClass={scale.name}
                            priceClass={scale.price}
                            disabled={pending || held}
                            onAdd={(tapped) => {
                              add(tapped, boardOutlet);
                            }}
                          />
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>

              <PaymentRow
                form={form}
                takesReference={takesReference}
                totalPaise={totalPaise}
                disabled={pending || held}
                timeSlotClock={timeSlotClock}
              />
            </section>

            <SaleColumn
              form={form}
              lines={basket}
              groups={
                groups?.map((group) => ({
                  key: lineKey(group.outlet, ''),
                  name: outletName(group.outlet),
                  subtotalPaise: group.priced?.totalPaise ?? 0,
                  roundOffPaise: group.priced?.invoice?.roundOffPaise ?? null,
                  lines: group.lines,
                })) ?? null
              }
              amounts={amounts}
              roundOffPaise={groups === null ? (priced?.invoice?.roundOffPaise ?? null) : null}
              totalPaise={totalPaise}
              pending={pending}
              sold={sold}
              outletName={outletName}
              stopped={stopped}
              refusal={refusal}
              onAdd={add}
              onQuantity={setQuantity}
              onRemoveOne={removeOne}
              onRemoveLine={removeLine}
              onClear={() => {
                finish({ sales: stopped?.sales ?? [], tickets: stopped?.issued ?? [] });
              }}
              onSubmit={takePayment}
            />
          </div>
        </Form>
      )}
    </div>
  );
}

/**
 * One admission on the rate board. A tap adds one of it to the party.
 *
 * **The count is a slate tag in the plate's corner** — the ink and the ground
 * reversed, the outlet plate's own pairing (`docs/design-language.md`), so no
 * new value. It is the one mark this screen adds to the board, and it earns it:
 * "how many adults did I ring up" is the question a cashier asks of the board
 * mid-party, and the answer should be where they are already looking.
 */
function AdmissionPlate({
  item,
  count,
  nameClass,
  priceClass,
  disabled,
  onAdd,
}: {
  item: HeldCatalogueItem;
  count: number;
  nameClass: string;
  priceClass: string;
  disabled: boolean;
  onAdd: (item: HeldCatalogueItem) => void;
}) {
  return (
    <Button
      variant="outline"
      // Neutral, not the accent: twelve green buttons would be twelve primary
      // actions. `h-full` fills the row band the scale decided; `relative` holds
      // the count tag in the corner.
      className="relative h-full min-h-touch w-full flex-col items-start justify-center gap-1 px-5 py-4 text-left whitespace-normal"
      disabled={disabled}
      onClick={() => {
        onAdd(item);
      }}
    >
      {/* `w-full` gives the name a definite width to break inside — the
          reasoning and the measurement are at the same span in `SellGoods`. */}
      <span className={`w-full font-medium break-words text-foreground ${nameClass}`}>
        {item.name}
      </span>

      {/* The price is the loud element, in mono with tabular figures: it is read
          aloud at the gate every time. */}
      <span className={`font-mono leading-none font-medium text-foreground numeric ${priceClass}`}>
        {formatPaise(item.sellingPaise)}
      </span>

      {count > 0 && (
        <span className="absolute top-3 right-3 rounded-lg bg-foreground px-2.5 py-1 font-mono text-xl leading-none text-background numeric">
          <span aria-hidden>×{count}</span>
          <span className="sr-only">, {count} in this sale</span>
        </span>
      )}
    </Button>
  );
}

/**
 * How they paid — the foot of the board, as on the shop counter.
 *
 * Rendered through the registry's `Form` context with no `<form>` element around
 * it: the `<form>` is in the sale column and holds only the total and the
 * submit, so no plate on the board is ever a submit button and Enter in the
 * reference field takes no money.
 */
function PaymentRow({
  form,
  takesReference,
  totalPaise,
  disabled,
  timeSlotClock,
}: {
  form: ReturnType<typeof useForm<PaymentValues>>;
  takesReference: boolean;
  /** What the party owes, for the change calculator. */
  totalPaise: number;
  disabled: boolean;
  /** Where the outlet asks for a time slot and the party has an invoice, the clock that prefills it (#277). */
  timeSlotClock: Clock | null;
}) {
  return (
    <div className="shrink-0 border-t border-border pt-4">
      <div className="flex flex-wrap items-end gap-x-6 gap-y-4">
        <FormField
          control={form.control}
          name="paymentMode"
          render={({ field }) => (
            <FormItem className="shrink-0">
              {/* **Deliberately not `FormLabel`**, for the reason `SellGoods`
                  gives: a radio group has no single control for a `<label
                  htmlFor>` to point at. */}
              <span id={`${field.name}-label`} className={fieldLabel}>
                How they paid
                <span aria-hidden className="text-destructive">
                  *
                </span>
              </span>

              <div
                role="radiogroup"
                aria-labelledby={`${field.name}-label`}
                aria-required
                className="flex gap-2"
              >
                {PAYMENT_MODES.map(({ mode, label }) => (
                  <Button
                    key={mode}
                    type="button"
                    role="radio"
                    aria-checked={field.value === mode}
                    // `secondary` rather than the accent: the one primary action
                    // on this screen is Take payment.
                    variant={field.value === mode ? 'secondary' : 'outline'}
                    size="touch"
                    className="min-w-28"
                    disabled={disabled}
                    onClick={() => {
                      field.onChange(mode);

                      // Switching to cash clears a reference somebody had typed;
                      // a cash ticket carrying one is refused outright
                      // (`TICKETING_PAYMENT_REFERENCE_NOT_ALLOWED`) — hours
                      // later, in the blocked pile.
                      if (mode === 'CASH') {
                        form.setValue('paymentReference', '', { shouldValidate: false });
                        form.clearErrors('paymentReference');
                      } else {
                        // Nothing is handed back on UPI or card, so a typed
                        // amount would only be a stale change figure — and a
                        // wrong line on the bill.
                        form.setValue('cashTendered', '', { shouldValidate: false });
                        form.clearErrors('cashTendered');
                      }
                    }}
                  >
                    {label}
                  </Button>
                ))}
              </div>

              <FormMessage />
            </FormItem>
          )}
        />

        {/* The Glass Bridge's window (#277), drawn only when asked. */}
        {timeSlotClock !== null && <TimeSlotFields clock={timeSlotClock} disabled={disabled} />}

        {/* Tendered and balance, as the shop counter has them (#269). */}
        <CashGiven totalPaise={totalPaise} disabled={disabled} />

        {/* Not rendered at all on cash, rather than disabled: the question does
            not apply, and an absent field says so. */}
        {takesReference && (
          <FormField
            control={form.control}
            name="paymentReference"
            render={({ field }) => (
              <FormItem className="min-w-56 flex-1">
                <FormLabel className={fieldLabel}>Reference (optional)</FormLabel>
                <FormControl
                  render={
                    <Input
                      className={`${fieldSurface} h-touch font-mono text-lg numeric`}
                      placeholder="431109882314"
                      autoComplete="off"
                      inputMode="numeric"
                      disabled={disabled}
                      {...field}
                    />
                  }
                />
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>
    </div>
  );
}

/**
 * The party being built, what it comes to, and the one button that sells it —
 * the shop counter's sale column, with admission's lines in it.
 *
 * `w-[22rem]` and the lines standing on the total (`mt-auto`) are `SellGoods`'
 * measured numbers, taken rather than re-derived: the two columns should be the
 * same column on one device.
 */
/** One outlet's share of a basket across outlets (#284), as the sale column draws it. */
interface ColumnGroup {
  readonly key: string;
  readonly name: string;
  /** Its invoice's total, to the rupee: what that outlet's sale charges. */
  readonly subtotalPaise: number;
  readonly roundOffPaise: number | null;
  readonly lines: readonly BasketLine[];
}

function SaleColumn({
  form,
  lines,
  groups,
  amounts,
  roundOffPaise,
  totalPaise,
  pending,
  sold,
  outletName,
  stopped,
  refusal,
  onAdd,
  onQuantity,
  onRemoveOne,
  onRemoveLine,
  onClear,
  onSubmit,
}: {
  form: ReturnType<typeof useForm<PaymentValues>>;
  lines: readonly BasketLine[];
  /** The lines by outlet, where they span more than the counter's own (#284). */
  groups: readonly ColumnGroup[] | null;
  /** What each line charges, by `lineKey`. */
  amounts: ReadonlyMap<string, number>;
  /** The invoice's round off, or `null` where nothing in the party is taxed — or it is per outlet. */
  roundOffPaise: number | null;
  totalPaise: number;
  pending: boolean;
  sold: Party | null;
  outletName: (outlet: LineOutlet) => string;
  stopped: Stopped | null;
  refusal: string | null;
  onAdd: (item: HeldCatalogueItem, outlet: LineOutlet) => void;
  onQuantity: (key: string, quantity: number) => void;
  onRemoveOne: (key: string) => void;
  onRemoveLine: (key: string) => void;
  onClear: () => void;
  onSubmit: (values: PaymentValues) => Promise<void>;
}) {
  const held = stopped !== null;

  /** What every line's row is handed, grouped or not. */
  const rowProps = {
    amounts,
    disabled: pending || held,
    onAdd,
    onQuantity,
    onRemoveOne,
    onRemoveLine,
  };

  /** Keeps the line just rung up in view — the reasoning is `SellGoods`'. */
  const linesRegion = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const region = linesRegion.current;

    if (region === null) return;

    // A held party opens at the top of its notice: what sold and what did not
    // is what has to be read first, and on a 625px window it does not all fit.
    // So does a sold one (#263): a typed party of 175 is 175 rows, and opened
    // at its tail the line saying it sold, for how much and how it was paid is
    // 11,000px up.
    region.scrollTop = stopped === null && lines.length > 0 ? region.scrollHeight : 0;
  }, [lines, stopped]);

  return (
    <aside
      aria-label="This sale"
      className="flex w-[22rem] shrink-0 flex-col rounded-2xl border border-border bg-card"
    >
      {/* `overflow-x-hidden` for the axis reason `SellGoods` gives at the same
          region; its `px-5` clears the focus ring on every edge. */}
      <div
        ref={linesRegion}
        className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto [mask-image:linear-gradient(to_bottom,transparent,#000_1.5rem)] px-5 py-3"
      >
        <div className="mt-auto">
          {refusal !== null && (
            <Alert variant="destructive" className="mb-4">
              <IconAlertTriangle />
              <AlertDescription>{refusal}</AlertDescription>
            </Alert>
          )}

          {/* While a party is held its notice stands in for the lines: they
              cannot be changed, and the plates still show every count. */}
          {stopped !== null ? (
            <StoppedNotice stopped={stopped} outletName={outletName} />
          ) : lines.length === 0 && sold !== null ? (
            <SoldNotice party={sold} outletName={outletName} />
          ) : lines.length === 0 ? (
            <p className="text-sm text-muted-foreground">Tap what they are buying.</p>
          ) : groups === null ? (
            <ul className="space-y-4">
              {lines.map((line) => (
                <BasketLineRow key={lineKey(line.outlet, line.item.id)} line={line} {...rowProps} />
              ))}
            </ul>
          ) : (
            // **By outlet, each with what its own sale charges (#284)** — the
            // subtotals are the invoices, and the total below is them added: the
            // number the cashier quotes, read off the screen.
            <div className="space-y-6">
              {groups.map((group) => (
                <div key={group.key} role="group" aria-label={group.name} className="space-y-4">
                  <div className="flex items-baseline gap-3 border-b border-border pb-2">
                    <span className={`min-w-0 flex-1 break-words ${tillPlate}`}>{group.name}</span>
                    <span className="shrink-0 font-mono text-lg font-medium text-foreground numeric">
                      {formatPaise(group.subtotalPaise)}
                    </span>
                  </div>

                  <ul className="space-y-4">
                    {group.lines.map((line) => (
                      <BasketLineRow
                        key={lineKey(line.outlet, line.item.id)}
                        line={line}
                        {...rowProps}
                      />
                    ))}
                  </ul>

                  {/* Each outlet's own, for the shop counter's reason: its lines
                      do not add up to its subtotal by this much. */}
                  {group.roundOffPaise !== null && (
                    <div className="flex items-baseline gap-x-4 text-sm text-muted-foreground">
                      <span>Round off</span>
                      <span className="ms-auto font-mono numeric">
                        {formatPaise(group.roundOffPaise)}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* The foot, at a fixed distance from the bottom of the counter whatever
          the party does above it: the datum, the total, and the press. */}
      <div className="shrink-0 px-5 pt-3 pb-4">
        <div className="h-[3px] w-full datum bg-foreground" />

        {/* The `<form>` holds only the total and the submit, for the reason
            `SellGoods` gives: the board is outside a form by construction, so no
            plate is a submit button and Enter in the reference takes no money. */}
        <form
          className="flex flex-col gap-3 pt-3"
          noValidate
          onSubmit={(event) => void form.handleSubmit(onSubmit)(event)}
        >
          {/* **Only where the party is taxed (#274)**, for the reason the shop
              counter gives: a taxed line carries paise and the invoice is taken
              to the rupee, so the lines do not add up to the total by this much.
              The paper prints the same line. */}
          {roundOffPaise !== null && (
            <div className="flex items-baseline gap-x-4 text-sm text-muted-foreground">
              <span>Round off</span>
              <span className="ms-auto font-mono numeric">{formatPaise(roundOffPaise)}</span>
            </div>
          )}

          <div className="flex flex-wrap items-baseline gap-x-4">
            <span className={tillPlate}>Total</span>
            <span className="ms-auto font-mono text-5xl leading-none font-medium text-foreground numeric">
              {formatPaise(totalPaise)}
            </span>
          </div>

          <Button
            type="submit"
            size="touch"
            className="w-full"
            disabled={pending || lines.length === 0}
            aria-label={pending || held ? undefined : `Take payment ${formatPaise(totalPaise)}`}
          >
            {pending ? 'Taking payment…' : held ? 'Try again' : 'Take payment'}
          </Button>

          {/* The way out of a part-sold party the retry cannot finish — an item
              gone from the list, say. What printed stays sold and stays on
              screen, numbered; a ticket the visitor no longer wants goes to a
              manager to void in the back office, as the notice says (#285).

              **Not offered when the till did not answer.** The party may have
              printed, and clearing would throw away the numbers the answer
              brings back — so Try again is the only press, and a till that
              never answers is restarted, as its notice says. */}
          {stopped !== null && stopped.unsold !== null && (
            <Button
              type="button"
              variant="outline"
              size="touch"
              className="w-full"
              disabled={pending}
              onClick={onClear}
            >
              Clear the basket
            </Button>
          )}
        </form>
      </div>
    </aside>
  );
}

/** One line of the party: what it charges, and the controls that change it. */
function BasketLineRow({
  line,
  amounts,
  disabled,
  onAdd,
  onQuantity,
  onRemoveOne,
  onRemoveLine,
}: {
  line: BasketLine;
  amounts: ReadonlyMap<string, number>;
  disabled: boolean;
  onAdd: (item: HeldCatalogueItem, outlet: LineOutlet) => void;
  onQuantity: (key: string, quantity: number) => void;
  onRemoveOne: (key: string) => void;
  onRemoveLine: (key: string) => void;
}) {
  const key = lineKey(line.outlet, line.item.id);

  return (
    <li className="space-y-2">
      <div className="flex items-baseline gap-3">
        <span className="min-w-0 flex-1 text-base font-medium break-words text-foreground">
          {line.item.name}
        </span>

        <span className="shrink-0 font-mono text-lg text-foreground numeric">
          {formatPaise(amounts.get(key) ?? 0)}
        </span>
      </div>

      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="icon-touch"
          disabled={disabled}
          aria-label={`One fewer ${line.item.name}`}
          onClick={() => {
            onRemoveOne(key);
          }}
        >
          <IconMinus />
        </Button>

        {/* Typed as well as stepped (#263): a party of 175 is not
            175 taps. `QuantityField` holds the bounds. */}
        <QuantityField
          name={line.item.name}
          quantity={line.quantity}
          disabled={disabled}
          onQuantity={(quantity) => {
            onQuantity(key, quantity);
          }}
        />

        <Button
          variant="outline"
          size="icon-touch"
          disabled={disabled || line.quantity >= TYPED_QUANTITY_MAX}
          aria-label={`One more ${line.item.name}`}
          onClick={() => {
            onAdd(line.item, line.outlet);
          }}
        >
          <IconPlus />
        </Button>

        {/* At the far end, away from the pair: taking the line off is
            a different act from taking one off it. */}
        <Button
          variant="outline"
          size="icon-touch"
          className="ms-auto"
          disabled={disabled}
          aria-label={`Take ${line.item.name} off this sale`}
          onClick={() => {
            onRemoveLine(key);
          }}
        >
          <IconTrash />
        </Button>
      </div>
    </li>
  );
}

/**
 * A party sold only in part, or not answered for — and exactly which admissions
 * are which.
 *
 * Red, because the system refused something: `docs/ui-conventions.md` spends red
 * on exactly that. The two lists are named so they can be read out, and the
 * numbers that printed are the ones the visitor is already holding.
 */
function StoppedNotice({
  stopped,
  outletName,
}: {
  stopped: Stopped;
  outletName: (outlet: LineOutlet) => string;
}) {
  return (
    <Alert variant="destructive" className="mb-4">
      <IconAlertTriangle />
      <AlertTitle>
        {stopped.unsold === null ? 'The till did not answer' : 'Only part of this party was sold'}
      </AlertTitle>
      <AlertDescription className="space-y-3">
        {/* The two lists first: which admissions went through and which did not
            is what the cashier acts on, and on a 625px window it is what shows
            before the column scrolls. */}
        {(stopped.sales.length > 0 || stopped.issued.length > 0) && (
          <div>
            <p id="stopped-sold" className="font-medium text-foreground">
              Sold and printed
            </p>
            <ul aria-labelledby="stopped-sold">
              {/* The taxed lines, one sale under one invoice (#274) — or one
                  per outlet that recorded (#284), each named. They stand
                  whatever happens to the rest: Try again sends only that. */}
              {stopped.sales.map(({ outlet, sale }) => (
                <li key={sale.id}>
                  <span className="font-mono text-foreground numeric">{sale.number}</span> ·{' '}
                  {stopped.at !== null && <>{outletName(outlet)} · </>}
                  {invoiceSummary(sale)}
                  {!sale.printed && ' · did not print'}
                </li>
              ))}
              {stopped.issued.map((ticket) => (
                <li key={ticket.id}>
                  <span className="font-mono text-foreground numeric">{ticket.number}</span> ·{' '}
                  {ticket.itemName}
                </li>
              ))}
            </ul>
          </div>
        )}

        {stopped.unsold !== null && (
          <div>
            <p id="stopped-missing" className="font-medium text-foreground">
              Not sold
            </p>
            <ul aria-labelledby="stopped-missing">
              {stopped.unsold.map(({ key, text }) => (
                <li key={key} className="numeric">
                  {text}
                </li>
              ))}
            </ul>
          </div>
        )}

        {stopped.unsold === null ? (
          <p>{stopped.message}</p>
        ) : (
          <>
            {/* Scoped, because the refusal copy speaks for the admission that
                stopped — "nothing was printed" — and the party above it did.
                Across outlets it is that outlet's alone (#284). */}
            <p>
              Why it stopped{stopped.at === null ? '' : ` at ${stopped.at}`}: {stopped.message}
            </p>
            <p>
              Try again sells only what is missing. Or clear the basket, and hand back the money for
              what was not sold.
            </p>
            {/* The way out of what recorded, now that nothing is corrected at
                the counter (#294): a manager refunds it from the number above —
                across outlets, each outlet's sale on its own. */}
            {stopped.sales.length > 0 && (
              <p>
                What was sold stays sold
                {stopped.at === null ? '' : ', each at its own outlet with its own slip'}. If the
                visitor no longer wants it, a manager refunds it in the back office from its number
                above.
              </p>
            )}
            {/* The tickets that printed cannot be voided here (#285). */}
            {stopped.issued.length > 0 && (
              <p>
                The tickets that printed stay sold. If the visitor no longer wants them, a manager
                voids them in the back office from the numbers above.
              </p>
            )}
          </>
        )}
      </AlertDescription>
    </Alert>
  );
}

/** `7 × Adult, 3 × Children` — what one sale holds, said back. */
const partyLines = (sale: RecordedSale): string =>
  sale.lines.map((line) => `${String(line.quantity)} × ${line.itemName}`).join(', ');

/** `Tax invoice C1-000041 · 7 × Adult, 3 × Children`. */
const invoiceSummary = (sale: RecordedSale): string =>
  sale.invoiceNumber === undefined
    ? partyLines(sale)
    : `Tax invoice ${sale.invoiceNumber} · ${partyLines(sale)}`;

/**
 * The party that just sold: its sale and every ticket, and nothing to press.
 *
 * The numbers are the largest thing on it, in mono with tabular figures, because
 * each is read aloud across a counter and typed back into the back office's
 * lookup. **A taxed party is one sale (#274)**, so it is one number with its
 * tax invoice under it. **Nothing on it is a control (#285, #294)**: voiding a
 * ticket and refunding a sale are both a manager's, in the back office, from
 * these numbers. The one control that must never exist is print-it-again.
 *
 * The one thing worth red is paper that did not print.
 */
function SoldNotice({
  party,
  outletName,
}: {
  party: Party;
  outletName: (outlet: LineOutlet) => string;
}) {
  const { sales, tickets } = party;
  const unprinted = tickets.filter((ticket) => !ticket.printed);
  const saleUnprinted = sales.some(({ sale }) => !sale.printed);
  const totalPaise =
    sales.reduce((sum, { sale }) => sum + sale.totalPaise, 0) +
    tickets.reduce((sum, ticket) => sum + ticket.amountPaise, 0);
  // One answer covered the whole party, so any part's is all of theirs — every
  // outlet's sale included (#284).
  const paymentMode = sales[0]?.sale.paymentMode ?? tickets[0]?.paymentMode;
  // Each sale named by its outlet only where the party spans more than one.
  const named = sales.some(({ outlet }) => outlet !== null);

  return (
    <Alert variant={unprinted.length > 0 || saleUnprinted ? 'destructive' : undefined}>
      {unprinted.length > 0 || saleUnprinted ? <IconPrinterOff /> : <IconCircleCheck />}
      <AlertTitle>
        {unprinted.length > 0 || saleUnprinted ? 'Sold, but the bill did not print' : 'Sold'} —{' '}
        {sales.length === 0 && (
          <>
            {tickets.length} {tickets.length === 1 ? 'ticket' : 'tickets'},{' '}
          </>
        )}
        <span className="font-mono numeric">{formatPaise(totalPaise)}</span>
        {/* **How it was recorded, said back** — the only chance anybody has to
            notice a card party that went down as cash. */}
        {paymentMode !== undefined && <> · {paymentModeLabel(paymentMode)}</>}
      </AlertTitle>
      <AlertDescription>
        <ul className="mt-2 space-y-2">
          {sales.map(({ outlet, sale }) => (
            <li key={sale.id} className="flex items-center gap-3">
              {!sale.printed && <IconPrinterOff aria-hidden className="size-4 shrink-0" />}

              <span className="min-w-0 flex-1">
                {/* Which outlet's slip this is, across outlets (#284): the
                    visitor holds one per outlet. */}
                {named && <span className="block text-sm">{outletName(outlet)}</span>}
                <span className="block font-mono text-xl text-foreground numeric">
                  {sale.number}
                </span>
                {/* The invoice number on a line of its own and never broken: it
                    is what the visitor's paper is headed with, and at 1000px
                    a shared line split it at its hyphen. */}
                {sale.invoiceNumber !== undefined && (
                  <span className="block text-sm">
                    Tax invoice{' '}
                    <span className="font-mono whitespace-nowrap text-foreground numeric">
                      {sale.invoiceNumber}
                    </span>
                  </span>
                )}
                <span className="block text-sm">
                  {partyLines(sale)}
                  {!sale.printed ? ' · did not print' : ''}
                </span>
              </span>
            </li>
          ))}

          {tickets.map((ticket) => (
            <li key={ticket.id} className="flex items-center gap-3">
              {!ticket.printed && <IconPrinterOff aria-hidden className="size-4 shrink-0" />}

              <span className="min-w-0 flex-1">
                <span className="block font-mono text-xl text-foreground numeric">
                  {ticket.number}
                </span>
                <span className="text-sm">
                  {ticket.itemName}
                  {!ticket.printed ? ' · did not print' : ''}
                </span>
              </span>
            </li>
          ))}
        </ul>

        {saleUnprinted && (
          // No reprint exists to fix it: the invoice is the entry ticket, and a
          // second copy is a free entry. The remedy is a fresh sale and a
          // refund of this one — the unprinted tickets' remedy below, word for
          // word in shape, since the refund is a manager's now too (#294).
          <p className="mt-3">
            This sale is recorded and will reach the office, but its entry ticket did not come out,
            and it cannot be printed again. Check the printer. Hand back what it cost, and sell the
            party again if they still want to come in. Then give a manager the number marked here to
            refund in the back office, so the takings still add up at close.
          </p>
        )}

        {unprinted.length > 0 && (
          // Said plainly, because the record cannot be fixed from here (#285):
          // the money is put right at the counter, and the void is a manager's
          // in the back office — the mark that explains, at close, a drawer
          // that no longer holds what these cost.
          <p className="mt-3">
            These are recorded and will reach the office, but their bill did not come out, and it
            cannot be printed again. Check the printer. Hand back what these cost, and sell them
            again if the visitor still wants to come in. Then give a manager the numbers marked here
            to void in the back office, so the takings still add up at close.
          </p>
        )}
      </AlertDescription>
    </Alert>
  );
}

/**
 * The board the skeleton is drawn at.
 *
 * Six slots is what it draws, so it takes the tier six items would get — a guess
 * in the middle moves least whichever way the answer falls.
 */
const SKELETON_BOARD = rateBoardScale(6);

/** A skeleton in the shape of the buttons, so nothing shifts when they land. */
function ItemsSkeleton() {
  return (
    <ul
      className={`grid ${SKELETON_BOARD.columns} ${SKELETON_BOARD.rows} content-start gap-4`}
      aria-hidden
      data-testid="till-items-skeleton"
    >
      {[0, 1, 2, 3, 4, 5].map((slot) => (
        <li key={slot} className="min-h-touch animate-pulse rounded-2xl bg-muted" />
      ))}
    </ul>
  );
}
